'use client';

import { useState, useEffect } from 'react';
import { Users, Home, Building2, Lightbulb, Factory, CloudSun, PiggyBank } from 'lucide-react';
import { Card } from '../atomic/Card';
import { InfoTooltip } from '../atomic/Tooltip';
import CitizenParticipationCalculatorMobile from './CitizenParticipationCalculatorMobile';

interface CitizenParticipationCalculatorProps {
  haushalte: number;
  gemeindeName: string;
  jahresPotenzial: number;
  energieProduktion?: {
    solar: number;
    wasser: number;
    biomasse: number;
    biogas: number;
  };
  energySupplier?: 'tiwag' | 'ikb';
}

export function CitizenParticipationCalculator({ 
  haushalte, 
  gemeindeName,
  jahresPotenzial,
  energieProduktion,
  energySupplier = 'tiwag'
}: CitizenParticipationCalculatorProps) {
  const [isMobile, setIsMobile] = useState<boolean | null>(null);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Marktdaten mit wissenschaftlichen Quellen
  const STROM_KOSTEN = {
    ikb: 0.242, // €/kWh IKB Standardtarif (Stand: Januar 2025)
    eeg: 0.184, // €/kWh aus Energiegemeinschaft (-24% lt. EnComm TU Wien 2024)
    ersparnis: 0.058 // €/kWh Ersparnis (5,8 cent)
  };
  
  const VERBRAUCH = {
    haushalt: 4000, // kWh/Jahr Durchschnittshaushalt (Quelle: E-Control Haushaltsdaten 2024)
    gewerbe: 12000, // kWh/Jahr kleiner Gewerbebetrieb (Quelle: WKO Energiekennzahlen 2024)
    pvErzeugung: 5250 // kWh/Jahr von 5 kWp Anlage (Quelle: PV Austria - 1050 kWh/kWp)
  };

  // Berechnungen für verschiedene Kundengruppen
  const haushaltErsparnis = Math.round(VERBRAUCH.haushalt * STROM_KOSTEN.ersparnis);
  const gewerbeErsparnis = Math.round(VERBRAUCH.gewerbe * STROM_KOSTEN.ersparnis);
  
  // Mit eigener PV: Zusätzlich bessere Einspeisetarife
  const pvEinspeiseBonus = 0.02; // 2 cent/kWh extra für Einspeisung in EEG
  const pvZusatzertrag = Math.round(VERBRAUCH.pvErzeugung * 0.7 * pvEinspeiseBonus); // 70% Einspeisung
  const pvGesamtVorteil = haushaltErsparnis + pvZusatzertrag;
  
  // Tagstromvorteil für Gewerbe (günstigerer EEG-Strom tagsüber)
  const tagstromAnteil = 0.7; // 70% des Verbrauchs tagsüber
  const tagstromBonus = 0.02; // 2 cent/kWh zusätzlicher Vorteil
  const tagstromErsparnis = Math.round(VERBRAUCH.gewerbe * tagstromAnteil * tagstromBonus);
  
  // Regionale Wertschöpfung
  const regionaleWertschoepfung = Math.round(haushaltErsparnis * 0.8);
  
  if (isMobile === null) return null;
  if (isMobile) {
    return (
      <CitizenParticipationCalculatorMobile
        totalYearlyProductionKWh={jahresPotenzial}
        gemeindeName={gemeindeName}
      />
    );
  }
  
  return (
    <Card className="p-6 sm:p-8 bg-white dark:bg-gray-900 shadow-sm">
      {/* Header mit Apple-typischer Typografie */}
      <div className="mb-6">
        <h3 className="text-xl tracking-tight font-medium text-gray-900 dark:text-white mb-1">
          Für Ihre Bürger
          <InfoTooltip 
            content={{
              title: "Vorteile der Energiegemeinschaft",
              description: "24% niedrigerer Strompreis für alle Teilnehmer der Energiegemeinschaft - 5,8 Cent günstiger pro kWh als der IKB Standardtarif. (Quelle: EnComm-Studie TU Wien 2024)"
            }}
          />
        </h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">
          Bei <span className="font-medium">{haushalte.toLocaleString('de-AT')}</span> Haushalten in {gemeindeName}
        </p>
      </div>

      {/* Einheitliche Design-Kacheln mit subtilen Schatten und Abrundungen */}
      <div className="space-y-4">
        {/* Durchschnittliche Ersparnis - Apple-typisches Card-Design */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 backdrop-blur-sm transition-all duration-200 ease-in-out hover:translate-y-[-2px] hover:shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-blue-50 dark:bg-blue-900/30">
              <Lightbulb className="h-5 w-5 text-blue-500 dark:text-blue-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Stromkosten-Ersparnis
                <InfoTooltip 
                  content={{
                    title: "-24% niedrigerer Strompreis",
                    description: `Standardtarif IKB: 24,2 ct/kWh (Stand: Jan 2025)\nEEG-Tarif: 18,4 ct/kWh\nErsparnis: 5,8 ct/kWh\n\nBei ${VERBRAUCH.haushalt.toLocaleString('de-AT')} kWh Jahresverbrauch:\n${VERBRAUCH.haushalt.toLocaleString('de-AT')} × 5,8 ct = €${haushaltErsparnis}/Jahr\n\nQuelle: E-Control Haushaltsdaten 2024`
                  }}
                />
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">Ohne eigene PV-Anlage</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-gray-900 dark:text-white">€{haushaltErsparnis}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">pro Jahr</p>
          </div>
        </div>

        {/* Mit eigener PV-Anlage */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 backdrop-blur-sm transition-all duration-200 ease-in-out hover:translate-y-[-2px] hover:shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-green-50 dark:bg-green-900/30">
              <Home className="h-5 w-5 text-green-500 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Mit eigener PV-Anlage
                <InfoTooltip 
                  content={{
                    title: "Doppelter Vorteil für PV-Besitzer",
                    description: `Stromkosten-Ersparnis: €${haushaltErsparnis}/Jahr\nPlus: Bessere Einspeisetarife in der EEG\n\n5 kWp PV-Anlage produziert ${VERBRAUCH.pvErzeugung.toLocaleString('de-AT')} kWh/Jahr\n70% Einspeisung × 2 ct/kWh Bonus = €${pvZusatzertrag}/Jahr\n\nGesamtvorteil: €${pvGesamtVorteil}/Jahr`
                  }}
                />
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">5 kWp Anlage</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-gray-900 dark:text-white">€{pvGesamtVorteil}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">pro Jahr</p>
          </div>
        </div>

        {/* Gewerbebetrieb */}
        <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/60 rounded-2xl p-4 backdrop-blur-sm transition-all duration-200 ease-in-out hover:translate-y-[-2px] hover:shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-purple-50 dark:bg-purple-900/30">
              <Building2 className="h-5 w-5 text-purple-500 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Gewerbebetrieb
                <InfoTooltip 
                  content={{
                    title: "Gewerbe-Ersparnis",
                    description: `Bei ${VERBRAUCH.gewerbe.toLocaleString('de-AT')} kWh Jahresverbrauch:\n${VERBRAUCH.gewerbe.toLocaleString('de-AT')} × 5,8 ct = €${gewerbeErsparnis}/Jahr\n\nGewerbetarife profitieren besonders von der 24% Ersparnis gegenüber IKB-Standardtarifen.`
                  }}
                />
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">12.000 kWh Jahresverbrauch</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-gray-900 dark:text-white">€{gewerbeErsparnis}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">pro Jahr</p>
          </div>
        </div>
        
        {/* Günstiger Tagstrom - Hervorgehoben */}
        <div className="flex items-center justify-between bg-amber-50 dark:bg-amber-900/20 rounded-2xl p-4 backdrop-blur-sm transition-all duration-200 ease-in-out hover:translate-y-[-2px] hover:shadow-md">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 flex items-center justify-center rounded-full bg-amber-100 dark:bg-amber-800/40">
              <CloudSun className="h-5 w-5 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
                Günstiger Tagstrom
                <InfoTooltip 
                  content={{
                    title: "Tagstrom-Vorteil für Gewerbe",
                    description: `70% des Gewerbeverbrauchs fallen tagsüber an (9-16 Uhr)\nGenau dann produzieren PV-Anlagen am meisten\n\nZusätzlicher Vorteil durch Überschuss-Strom:\n${(VERBRAUCH.gewerbe * tagstromAnteil).toLocaleString('de-AT')} kWh × 2 ct = €${tagstromErsparnis}/Jahr`
                  }}
                />
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">9-16 Uhr für Gewerbebetriebe</p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xl font-semibold text-gray-900 dark:text-white">€{tagstromErsparnis}</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">pro Jahr zusätzlich</p>
          </div>
        </div>
      </div>

      {/* Preisvorteil Highlight */}
      <div className="mt-6 p-5 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/20 rounded-2xl shadow-sm transition-all duration-200 ease-in-out hover:shadow-md">
        <div className="flex items-center mb-2">
          <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-800/60 mr-3">
            <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          </div>
          <p className="text-sm font-medium text-gray-800 dark:text-gray-200">
            Strompreisvorteil
            <InfoTooltip 
              content={{
                title: "24% günstiger als IKB",
                description: "Alle Teilnehmer der Energiegemeinschaft profitieren vom reduzierten Strompreis von 18,4 ct/kWh statt 24,2 ct/kWh - eine Ersparnis von 5,8 Cent pro Kilowattstunde."
              }}
            />
          </p>
        </div>
        <div className="flex justify-between items-end mt-3">
          <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
            Für alle Teilnehmer<br/>der Energiegemeinschaft
          </p>
          <div className="text-right">
            <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">-24%</p>
            <p className="text-xs text-gray-500 dark:text-gray-400">gegenüber IKB</p>
          </div>
        </div>
      </div>

      {/* Regional value highlight - Apple-typischer Call-to-Action-Bereich */}
      <div className="mt-6 px-5 py-6 bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/10 rounded-2xl text-center shadow-sm">
        <div className="w-14 h-14 mx-auto flex items-center justify-center rounded-full bg-green-100 dark:bg-green-800/30 mb-3 shadow-sm">
          <PiggyBank className="h-7 w-7 text-green-600 dark:text-green-400" />
        </div>
        <p className="text-base font-medium text-gray-800 dark:text-gray-200 mb-1">
          <span className="text-xl font-semibold text-green-600 dark:text-green-400">€{regionaleWertschoepfung}</span> pro Haushalt
          <InfoTooltip 
            content={{
              title: "Regionale Wertschöpfung",
              description: `Von den €${haushaltErsparnis} Ersparnis bleiben 80% in der Region.\n\nDas Geld fließt an lokale Energieerzeuger und stärkt die regionale Wirtschaft statt an große Energiekonzerne.`
            }}
          />
        </p>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          bleiben in der regionalen Wertschöpfung
        </p>
      </div>
    </Card>
  );
}